<template>
  <div class="md:w-368 mx-auto">
    <JobPostScheduleCard
      id="schedule-type-one-time"
      title="One-time sitter"
      body="Date night, upcoming event, backup"
      :shadow="false"
      class="mb-16"
    >
      <CalendarIconOneTime class="mx-16 my-24" color="#FF0064" />
    </JobPostScheduleCard>

    <Card :clickable="false" class="w-full md:p-24">
      <form>
        <div class="mb-32">
          <poppins-date-picker
            id="one-time-schedule-datepicker"
            v-model="startDate"
            labelName="Start date"
            :minDate="minStartDate"
            :maxDate="maxStartDate"
            :status="datepickerStatus"
            :helperText="errors.startDate"
            class="mb-16 w-full"
            @input="$delete(errors, 'startDate')"
          />
          <poppins-time-picker
            id="one-time-schedule-start"
            v-model="startTime"
            class="mb-16"
            labelName="Start time"
            :start="startRange[0]"
            :end="startRange[1]"
            :status="startTimeStatus"
            :helperText="errors.startTime"
            @input="$delete(errors, 'startTime')"
          />
          <poppins-time-picker
            id="one-time-schedule-end"
            v-model="endTime"
            class="mb-16"
            labelName="End time"
            :start="endRange[0]"
            :end="endRange[1]"
            :status="endTimeStatus"
            :helperText="errors.endTime"
            @input="$delete(errors, 'endTime')"
          />
          <Checkbox v-model="startTimeFlexible" labelName="Start time is flexible" checkboxId="start-time-flexible" />
          <div>
            <p>Duration: {{ duration }}</p>
          </div>
        </div>
        <div class="flex flex-row-reverse">
          <RouteStepperNextButton
            :stepData="reviewSection"
            :draftData="draftData"
            :validate="validate"
            @errors="errors = $event"
          />
        </div>
      </form>
    </Card>
  </div>
</template>

<script lang="ts">
import { add, set, intervalToDuration, startOfDay, endOfDay, addHours, addDays, parseISO, formatISO, format } from 'date-fns';
import { PropType } from 'vue';
import PoppinsDatePicker from '@/components/PoppinsDatePicker/PoppinsDatePicker.vue';
import Checkbox from '@/components/checkbox/Checkbox.vue';
import CalendarIconOneTime from '@/graphics/icons/CalendarIconOneTime.vue';
import JobPostScheduleCard from './JobPostScheduleCard.vue';
import PoppinsTimePicker from '@/components/timepicker/PoppinsTimePicker.vue';
import { duration as calculateDuration } from '@/lib/time';
import { minStartDate, maxStartDate } from './schedule_helpers';
import helperStatus from '@/helpers/status';
import Card from '@/components/card/Card.vue';
import JobPostScheduleOneTimeReview from './JobPostScheduleOneTimeReview.vue';
import { ReviewSection } from '@/components/route_stepper/types';
import { JobPostDraft } from '../types';
import RouteStepperNextButton from '../../../components/route_stepper/RouteStepperNextButton.vue';
import { OneTimeJobSchedule } from '@/store/modules/job/types';

interface Errors {
  startDate?: string;
  startTime?: string;
  endTime?: string;
}
interface JobPostScheduleOneTimeData {
  startTimeFlexible: boolean;
  startDate: Date | null;
  startTime: Date | null;
  endTime: Date | null;
  startRange: Date[];
  endRange: Date[];
  errors: Errors;
}

export default {
  name: 'JobPostScheduleOneTime',
  components: {
    PoppinsDatePicker,
    CalendarIconOneTime,
    JobPostScheduleCard,
    Checkbox,
    PoppinsTimePicker,
    Card,
    RouteStepperNextButton,
  },
  props: {
    draft: {
      type: Object as PropType<JobPostDraft>,
      required: true,
    },
  },
  data(): JobPostScheduleOneTimeData {
    const schedule = this.draft?.schedule?.oneTimeJobSchedule;
    const startDate = schedule?.startDate ? parseISO(schedule.startDate) : null;
    const startTime = schedule?.startTime ? parseISO(schedule.startTime) : null;
    const endTime = schedule?.endTime ? parseISO(schedule.endTime) : null;
    const startTimeFlexible = schedule?.isStartTimeFlexible || false;

    return {
      startTimeFlexible,
      startDate,
      startTime,
      endTime,
      startRange: [],
      endRange: [],
      errors: {},
    };
  },
  computed: {
    minStartDate,
    maxStartDate,
    duration(): string {
      if (this.startTime && this.endTime) {
        return calculateDuration(this.startTime, this.endTime);
      }

      return '';
    },
    datepickerStatus(): helperStatus {
      if (this.errors.startDate) {
        return helperStatus.Error;
      }
      return helperStatus.Neutral;
    },
    startTimeStatus(): helperStatus {
      if (this.errors.startTime) {
        return helperStatus.Error;
      }
      return helperStatus.Neutral;
    },
    endTimeStatus(): helperStatus {
      if (this.errors.endTime) {
        return helperStatus.Error;
      }
      return helperStatus.Neutral;
    },
    noStartDateError(): Boolean {
      return !this.startDate;
    },
    noStartTimeError(): Boolean {
      return !this.startTime;
    },
    noEndTimeError(): Boolean {
      return !this.endTime;
    },
    reviewSection(): ReviewSection | undefined {
      if (!this.startDate || !this.startTime || !this.endTime) {
        return undefined;
      }

      return {
        title: 'Schedule',
        component: JobPostScheduleOneTimeReview,
        props: {
          formattedStartDate: format(this.startDate, 'eeee, MMMM d, yyyy'),
          startTime: this.startTime,
          endTime: this.endTime,
          startTimeFlexible: this.startTimeFlexible,
        },
        editPath: this.$route.path,
      };
    },
    oneTimeJobSchedule(): OneTimeJobSchedule | undefined {
      if (!this.startDate || !this.startTime || !this.endTime) {
        return undefined;
      }

      return {
        startDate: formatISO(this.startDate, { representation: 'date' }),
        startTime: format(this.startTime, 'HH:mm'),
        endTime: format(this.endTime, 'HH:mm'),
        isStartTimeFlexible: this.startTimeFlexible,
      };
    },
    draftData(): JobPostDraft {
      return {
        schedule: { oneTimeJobSchedule: this.oneTimeJobSchedule },
        careType: 'BABYSITTER',
      };
    },
  },
  watch: {
    startDate(day: Date) {
      if (day && this.startTime) {
        const oldStart = this.startTime;
        // these attempt to update the startTime to the same time on the new date
        this.startTime = set(this.startTime, {
          year: day?.getFullYear(),
          month: day?.getMonth(),
          date: day?.getDate(),
        });

        if (this.endTime) {
          const diff = intervalToDuration({ start: oldStart, end: this.endTime });
          this.endTime = add(this.startTime, diff);
        }
        this.calculateStartRange();
      }
    },
    startTime(newValue: Date, oldValue) {
      if (newValue && newValue !== oldValue) {
        this.calculateEndRange();
      }
    },
  },
  created() {
    this.calculateStartRange();
    this.calculateEndRange();
  },

  methods: {
    calculateStartRange() {
      let d = new Date();
      if (this.startDate) {
        d = this.startDate;
      }

      const s = startOfDay(d);
      const e = endOfDay(d);

      this.startRange = [s, e];
    },
    calculateEndRange() {
      if (!this.startTime) {
        this.endRange = [];
        return;
      }

      if (!this.endTime || this.startTime < this.endTime) {
        const s = addHours(this.startTime, 1);
        const e = addDays(this.startTime, 1);

        this.endRange = [s, e];
      }
    },
    validate(): Errors {
      const errors: Errors = {};

      if (!this.startDate) {
        errors.startDate = 'Please select a start date.';
      }
      if (!this.startTime) {
        errors.startTime = 'Please select a start time.';
      }
      if (!this.endTime) {
        errors.endTime = 'Please select an end time.';
      }
      if (this.endTime && this.startTime && this.endTime <= this.startTime) {
        errors.endTime = 'Choose an end time later than the start time.';
      }
      return errors;
    },
  },
};
</script>
